import { SSE_EVENT_TYPES, TextDeltaEventSchema, ToolCallEventSchema, ToolInputAvailableSchema, ToolResultEventSchema, ToolOutputAvailableSchema, FinishEventSchema, } from '../models/sse-event.schema';
/**
 * Parse a single SSE data line into a typed event and dispatch to callbacks.
 *
 * Handles the Vercel AI SDK UI Message Stream format:
 * - text-delta: { type: "text-delta", delta: "..." }
 * - tool-call / tool-input-available: tool invocation with args/input
 * - tool-result / tool-output-available: tool response with result/output
 * - finish:     { type: "finish", finishReason }
 * - start, start-step, text-start, text-end, finish-step, tool-input-start: ignored (lifecycle)
 * - data-*: custom data events forwarded via onData callback
 */
function dispatchEvent(jsonString, callbacks) {
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== 'object' || !parsed.type)
        return;
    switch (parsed.type) {
        case SSE_EVENT_TYPES.TEXT_DELTA: {
            const event = TextDeltaEventSchema.parse(parsed);
            callbacks.onTextDelta?.(event.delta);
            break;
        }
        case SSE_EVENT_TYPES.TOOL_CALL: {
            const event = ToolCallEventSchema.parse(parsed);
            callbacks.onToolCall?.(event);
            break;
        }
        case SSE_EVENT_TYPES.TOOL_INPUT_AVAILABLE: {
            const event = ToolInputAvailableSchema.parse(parsed);
            callbacks.onToolCall?.({
                type: 'tool-call',
                toolCallId: event.toolCallId,
                toolName: event.toolName,
                args: event.input,
            });
            break;
        }
        case SSE_EVENT_TYPES.TOOL_RESULT: {
            const event = ToolResultEventSchema.parse(parsed);
            callbacks.onToolResult?.(event);
            break;
        }
        case SSE_EVENT_TYPES.TOOL_OUTPUT_AVAILABLE: {
            const event = ToolOutputAvailableSchema.parse(parsed);
            callbacks.onToolResult?.({
                type: 'tool-result',
                toolCallId: event.toolCallId,
                result: event.output,
            });
            break;
        }
        case SSE_EVENT_TYPES.FINISH: {
            const event = FinishEventSchema.parse(parsed);
            callbacks.onFinish?.(event);
            break;
        }
        // Lifecycle events — no action needed
        case SSE_EVENT_TYPES.START:
        case SSE_EVENT_TYPES.START_STEP:
        case SSE_EVENT_TYPES.TEXT_START:
        case SSE_EVENT_TYPES.TEXT_END:
        case SSE_EVENT_TYPES.FINISH_STEP:
        case SSE_EVENT_TYPES.TOOL_INPUT_START:
            break;
        default: {
            if (parsed.type?.startsWith('data-')) {
                callbacks.onData?.({ type: parsed.type, data: parsed.data });
            }
            break;
        }
    }
}
/**
 * Process a ReadableStream of SSE data, parsing events and dispatching to callbacks.
 *
 * SSE format: lines starting with "data: " followed by JSON, separated by empty lines.
 */
export async function processStream(stream, callbacks) {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
        let done = false;
        while (!done) {
            const result = await reader.read();
            done = result.done;
            if (done)
                break;
            const value = result.value;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            // Keep the last partial line in the buffer
            buffer = lines.pop() ?? '';
            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed)
                    continue;
                if (trimmed.startsWith('data: ')) {
                    const data = trimmed.slice(6);
                    if (data === '[DONE]')
                        continue;
                    try {
                        dispatchEvent(data, callbacks);
                    }
                    catch (parseError) {
                        callbacks.onError?.(parseError instanceof Error
                            ? parseError
                            : new Error(`SSE parse error: ${String(parseError)}`));
                    }
                }
            }
        }
        // Process remaining buffer
        if (buffer.trim()) {
            const trimmed = buffer.trim();
            if (trimmed.startsWith('data: ')) {
                const data = trimmed.slice(6);
                if (data !== '[DONE]') {
                    try {
                        dispatchEvent(data, callbacks);
                    }
                    catch (parseError) {
                        callbacks.onError?.(parseError instanceof Error
                            ? parseError
                            : new Error(`SSE parse error: ${String(parseError)}`));
                    }
                }
            }
        }
    }
    catch (error) {
        callbacks.onError?.(error instanceof Error ? error : new Error(`Stream error: ${String(error)}`));
    }
    finally {
        reader.releaseLock();
    }
}
export const sseStreamService = {
    processStream,
};
